import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Clock, ShieldCheck, Sparkles, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import VisitorLeadPopup from "@/components/VisitorLeadPopup";
import RoyalAIBanner from "@/components/RoyalAIBanner";
import LiveActivityTicker from "@/components/LiveActivityTicker";
import WhySmartBanner from "@/components/WhySmartBanner";
import AdBanner from "@/components/AdBanner";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const HERO_IMG =
    "https://images.pexels.com/photos/6196677/pexels-photo-6196677.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

const CATEGORIES = [
    {
        id: "Cleaning",
        title: "Cleaning",
        sub: "24/7 cleaners",
        img: "https://images.pexels.com/photos/6196677/pexels-photo-6196677.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        span: "md:col-span-2 md:row-span-2",
    },
    {
        id: "Beauty",
        title: "Beauty & Glam",
        sub: "Hair · Nails · Lash · Wax · Barber",
        img: "https://images.pexels.com/photos/11360229/pexels-photo-11360229.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        span: "md:col-span-2",
    },
    {
        id: "Childcare",
        title: "Babysitting",
        sub: "Hourly · day · overnight",
        img: "https://images.pexels.com/photos/11369160/pexels-photo-11369160.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        span: "md:col-span-2",
    },
    {
        id: "Chef",
        title: "Private Chef",
        sub: "Dinner · Events · Meal prep",
        img: "https://images.pexels.com/photos/27556985/pexels-photo-27556985.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        span: "",
    },
    {
        id: "Decor",
        title: "Event Decor",
        sub: "Parties · Weddings",
        img: "https://images.pexels.com/photos/20680118/pexels-photo-20680118.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        span: "",
    },
    {
        id: "Grocery",
        title: "Grocery Shopper",
        sub: "Budget + list · door drop",
        img: "https://images.pexels.com/photos/4393668/pexels-photo-4393668.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        span: "",
    },
    {
        id: "Auto",
        title: "Mobile Car Wash",
        sub: "Anywhere · any time",
        img: "https://images.pexels.com/photos/20051462/pexels-photo-20051462.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        span: "",
    },
    {
        id: "Transport",
        title: "Transport",
        sub: "Drivers · airport · rental",
        img: "https://images.pexels.com/photos/8425392/pexels-photo-8425392.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        span: "",
    },
    {
        id: "Handyman",
        title: "Handyman",
        sub: "AC · plumbing · painter · trees",
        img: "https://images.pexels.com/photos/8961065/pexels-photo-8961065.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        span: "",
    },
    {
        id: "Laundry",
        title: "Laundry",
        sub: "Pickup + delivery",
        img: "https://images.pexels.com/photos/33784705/pexels-photo-33784705.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        span: "",
    },
    {
        id: "Moving",
        title: "Moving & Hauling",
        sub: "SMART Haul · SMART Move",
        img: "https://images.pexels.com/photos/7464684/pexels-photo-7464684.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        span: "md:col-span-2",
        badge: "NEW",
    },
];

const TRUST = [
    "Verified Professionals",
    "Background Checked",
    "Insured & Bonded",
    "60-Second Booking",
    "24/7 Express Response",
    "VIP Concierge",
    "Loyalty Rewards",
    "Same-Day Glam",
];

export default function LandingPage() {
    const navigate = useNavigate();
    const [stats, setStats] = useState({ providers: 120, bookings: 4800 });

    useEffect(() => {
        api.get("/services").catch(() => {}); // warm up
    }, []);

    return (
        <div className="min-h-screen bg-background relative overflow-hidden" data-testid="landing-page">
            <Header />

            {/* Live activity ticker — social-proof strip, hides when zero activity */}
            <LiveActivityTicker />

            {/* HERO */}
            <section className="relative" data-testid="hero-section">
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMG}
                        alt="Luxury tropical night"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/60" />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background" />
                </div>

                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-32 lg:pt-32 lg:pb-44">
                    {/* ROYAL AI KNIGHT — Front-line billboard (CEO directive Feb 2026) */}
                    <div className="mb-10 fade-up" data-testid="hero-royal-ai-billboard">
                        <RoyalAIBanner />
                    </div>

                    <div className="overline fade-up" data-testid="hero-llc-kicker">
                        <span className="text-amber-300">SMART DEMANDS 24/7 LIMITED</span>
                        <span className="text-muted-foreground"> · Jamaica · Premium On-Demand</span>
                    </div>
                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light tracking-tighter leading-[0.95] mt-6 max-w-4xl fade-up fade-up-d1">
                        Your demands,<br />
                        <span className="italic gold-grad">24/7.</span><br />
                        Anytime, Anywhere.
                    </h1>
                    <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed fade-up fade-up-d2">
                        Cleaners, barbers, chefs, sitters, drivers, handymen — book the right SMART pro across Kingston, Montego Bay and Ocho Rios in under sixty seconds.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4 fade-up fade-up-d3">
                        <Button
                            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm uppercase tracking-[0.2em] text-xs h-12 px-7"
                            onClick={() => navigate("/services")}
                            data-testid="hero-book-now-btn"
                        >
                            Book Now <ArrowRight size={14} className="ml-2" strokeWidth={1.5} />
                        </Button>
                        <Button
                            className="rounded-sm uppercase tracking-[0.2em] text-xs h-12 px-7 bg-background/40 border border-[#FFD700]/50 text-[#FFD700] hover:bg-[#FFD700]/10"
                            onClick={() => navigate("/join")}
                            data-testid="hero-join-btn"
                        >
                            Join SMART
                        </Button>
                        <Button
                            variant="outline"
                            className="rounded-sm uppercase tracking-[0.2em] text-xs h-12 px-7 bg-transparent border-white/15 text-foreground hover:bg-white/5"
                            onClick={() => navigate("/vip")}
                            data-testid="hero-vip-btn"
                        >
                            Discover VIP
                        </Button>
                    </div>

                    <div className="mt-20 grid grid-cols-3 gap-6 max-w-2xl fade-up fade-up-d4">
                        <Stat label="Verified pros" value={`${stats.providers}+`} />
                        <Stat label="Bookings" value={`${stats.bookings.toLocaleString()}+`} />
                        <Stat label="Express response" value="< 1hr" />
                    </div>
                </div>
            </section>

            {/* QUICK CATEGORIES STRIP — every service one tap away */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 -mt-12 lg:-mt-20 relative z-10" data-testid="categories-quick-grid">
                <div className="bg-card/95 backdrop-blur border border-white/10 rounded-sm p-5 lg:p-7 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)]">
                    <div className="flex items-baseline justify-between mb-5">
                        <div className="overline">Pick a service</div>
                        <Link
                            to="/services"
                            className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:gold-text transition-colors"
                            data-testid="all-services-link"
                        >
                            All services →
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 lg:grid-cols-11 gap-3">
                        {CATEGORIES.map((c) => (
                            <Link
                                key={c.id}
                                to={`/services?category=${c.id}`}
                                className="group flex flex-col items-center gap-2 p-3 rounded-sm border border-white/5 hover:border-primary/40 hover:bg-primary/5 transition-all"
                                data-testid={`quick-cat-${c.id.toLowerCase()}`}
                            >
                                <div className="w-12 h-12 rounded-sm overflow-hidden border border-white/10 group-hover:border-primary/40 transition-colors">
                                    <img src={c.img} alt={c.title} className="w-full h-full object-cover" />
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.15em] text-center text-muted-foreground group-hover:text-foreground transition-colors leading-tight">
                                    {c.title}
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* TRUST MARQUEE */}
            <section className="border-y border-white/5 bg-card/40 overflow-hidden" data-testid="trust-marquee">
                <div className="py-8 flex marquee-track" style={{ width: "max-content" }}>
                    {[...TRUST, ...TRUST].map((t, i) => (
                        <div
                            key={`${t}-${i < TRUST.length ? "a" : "b"}`}
                            className="px-12 font-serif text-2xl text-muted-foreground italic whitespace-nowrap"
                        >
                            {t} <span className="gold-text not-italic mx-3">✦</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* PAID AD SLOT — landing_hero */}
            <section className="max-w-6xl mx-auto px-5 lg:px-8 pt-8" data-testid="landing-ad-slot">
                <AdBanner slot="landing_hero" />
            </section>

            {/* WHY SMART — reassurance pillars for first-time link visitors */}
            <WhySmartBanner />

            {/* SERVICE BENTO */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="services-bento">
                <div className="grid lg:grid-cols-12 gap-10 mb-16">
                    <div className="lg:col-span-5">
                        <div className="overline mb-4">The Catalog</div>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-tight">
                            Every service<br />
                            <span className="italic">your life</span> needs.
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                        <p className="text-base text-muted-foreground leading-relaxed">
                            Eleven categories, dozens of services, hundreds of vetted professionals. From a 1-hour express clean to a full-truck home move on a Sunday afternoon — SMART handles it.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-5 auto-rows-[220px]">
                    {CATEGORIES.map((c) => (
                        <Link
                            key={`bento-${c.id}`}
                            to={`/services?category=${c.id}`}
                            className={`group relative overflow-hidden border border-white/5 bg-card hover:border-primary/40 transition-all duration-500 ${c.span}`}
                            data-testid={`bento-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <img
                                src={c.img}
                                alt={c.title}
                                className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
                            {c.badge && (
                                <div className="absolute top-4 right-4 z-10 px-2.5 py-1 rounded-sm bg-primary text-primary-foreground text-[9px] uppercase tracking-[0.25em] font-semibold shadow-lg">
                                    {c.badge}
                                </div>
                            )}
                            <div className="relative h-full flex flex-col justify-end p-6 lg:p-8">
                                <div className="overline mb-2">{c.sub}</div>
                                <div className="font-serif text-2xl lg:text-3xl">{c.title}</div>
                                <div className="mt-3 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                                    Browse <ArrowRight size={12} strokeWidth={1.5} />
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 border-t border-white/5" data-testid="how-it-works">
                <div className="overline mb-4">The SMART Way</div>
                <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter mb-16 max-w-3xl">
                    Three steps. Sixty seconds. <span className="italic gold-grad">Effortless.</span>
                </h2>

                <div className="grid md:grid-cols-3 gap-px bg-white/5">
                    {[
                        { n: "01", title: "Choose your service", desc: "Browse cleaning, beauty, laundry or handyman. Pick a tier and timeslot.", icon: Sparkles },
                        { n: "02", title: "We dispatch a vetted pro", desc: "Background-checked, insured and rated 4.8+. Express options available.", icon: ShieldCheck },
                        { n: "03", title: "Track. Pay. Repeat.", desc: "Live status, in-app secure payments, loyalty points on every booking.", icon: Clock },
                    ].map((step) => (
                        <div key={`how-${step.n}`} className="bg-background p-10 lg:p-12 group hover:bg-card transition-colors">
                            <step.icon size={28} strokeWidth={1} className="gold-text mb-8" />
                            <div className="overline mb-3">Step {step.n}</div>
                            <h3 className="font-serif text-2xl mb-3">{step.title}</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* VIP TEASER */}
            <section className="relative overflow-hidden border-t border-white/5" data-testid="vip-teaser">
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage:
                            "url(https://images.unsplash.com/photo-1647104568641-bd427704384c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdvbGQlMjB0ZXh0dXJlJTIwZGFya3xlbnwwfHx8fDE3NzczODg4ODJ8MA&ixlib=rb-4.1.0&q=85)",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-background/80" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 text-center">
                    <div className="overline mb-4">SMART Privée</div>
                    <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none">
                        Become a <span className="italic gold-grad">VIP Member.</span>
                    </h2>
                    <p className="mt-8 text-base text-muted-foreground max-w-xl mx-auto leading-relaxed">
                        Three tiers of indulgence. Weekly cleaning, unlimited express bookings, monthly glam, and a personal concierge — for the ones who want everything taken care of.
                    </p>
                    <div className="mt-10 flex justify-center gap-3 flex-wrap">
                        <span className="px-4 py-2 border border-white/10 text-xs uppercase tracking-[0.2em]">Gold</span>
                        <span className="px-4 py-2 border border-primary/40 text-xs uppercase tracking-[0.2em] gold-text">Diamond</span>
                        <span className="px-4 py-2 border border-white/10 text-xs uppercase tracking-[0.2em]">Platinum</span>
                    </div>
                    <Button
                        className="mt-10 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm uppercase tracking-[0.2em] text-xs h-12 px-7"
                        onClick={() => navigate("/vip")}
                        data-testid="vip-explore-btn"
                    >
                        Explore VIP <ArrowRight size={14} className="ml-2" strokeWidth={1.5} />
                    </Button>
                </div>
            </section>

            {/* FREELANCER CTA */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 border-t border-white/5" data-testid="freelancer-cta">
                <div className="grid md:grid-cols-2 gap-16 items-center">
                    <div>
                        <div className="overline mb-4">For Freelancers</div>
                        <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter mb-6 leading-tight">
                            Turn your skills<br />
                            into <span className="italic gold-grad">real income.</span>
                        </h2>
                        <p className="text-base text-muted-foreground leading-relaxed mb-8">
                            Hairdressers, nail techs, makeup artists, masseuses, handymen and cleaners — SMART is Jamaica&apos;s fastest-growing on-demand network. Set your own schedule. Get paid weekly.
                        </p>
                        <ul className="space-y-3 text-sm text-muted-foreground mb-10">
                            {["Weekly payouts", "Verified profile badge", "Free training workshops", "Referral sign-up bonuses"].map((p) => (
                                <li key={p} className="flex items-center gap-3">
                                    <Star size={12} strokeWidth={1.5} className="gold-text" /> {p}
                                </li>
                            ))}
                        </ul>
                        <Button
                            variant="outline"
                            className="rounded-sm uppercase tracking-[0.2em] text-xs h-12 px-7 bg-transparent border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground"
                            onClick={() => navigate("/become-provider")}
                            data-testid="freelancer-apply-btn"
                        >
                            Apply Now
                        </Button>
                    </div>
                    <div className="relative aspect-[4/5] overflow-hidden border border-white/5">
                        <img
                            src="/img/barber.png"
                            alt="Featured SMART pro — Black Jamaican barber"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                        <div className="absolute bottom-8 left-8 right-8">
                            <div className="overline mb-2">Featured Pro</div>
                            <div className="font-serif text-2xl">Average pro earns $80,000 JMD/week</div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
            <VisitorLeadPopup />
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="border-l border-white/10 pl-4">
            <div className="font-serif text-3xl tracking-tight">{value}</div>
            <div className="overline mt-1">{label}</div>
        </div>
    );
}
